import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { alpha, styled } from "@mui/material/styles";

import { MASCOT_PALETTE } from "@/components/Others/MascotPanel";
import { colors } from "@/theme";

/* The In-Play strip under the banner. */

/**
 * The line under the In-Play banner: the live pulse and the counts on the
 * left, when the figures were last confirmed and the refresh button on the
 * right. Wraps on a phone.
 */
export const LiveStripRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 12,
  marginTop: 16,
  padding: "10px 14px",
  borderRadius: 12,
  border: `1px solid ${alpha(colors.prime, 0.28)}`,
  backgroundColor: alpha(colors.prime, 0.06),
  ...theme.applyStyles("dark", {
    border: `1px solid ${alpha(MASCOT_PALETTE.sky, 0.24)}`,
    background: `linear-gradient(135deg, ${MASCOT_PALETTE.night}, ${MASCOT_PALETTE.deep})`,
  }),
}));

/** "Live now", in the deep ink of the light scheme and the sky blue of the dark one. */
export const LiveEyebrow = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  color: MASCOT_PALETTE.deepInk,
  ...theme.applyStyles("dark", { color: MASCOT_PALETTE.sky }),
})) as typeof Stack;

/** The strip's figures: how many matches are on, in how many sports. */
export const LiveStats = styled(Stack)({
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 8,
});

/** One figure, on the tile the mascot pages give a count. */
export const LiveStat = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 12px",
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 700,
  fontVariantNumeric: "tabular-nums",
  color: MASCOT_PALETTE.deepInk,
  backgroundColor: alpha(colors.prime, 0.12),
  boxShadow: `inset 0 0 0 1px ${alpha(colors.prime, 0.28)}`,
  ...theme.applyStyles("dark", {
    color: MASCOT_PALETTE.sky,
    backgroundColor: alpha(MASCOT_PALETTE.sky, 0.12),
    boxShadow: `inset 0 0 0 1px ${alpha(MASCOT_PALETTE.sky, 0.32)}`,
  }),
}));

/** The "updated … ago" line with the refresh button beside it, kept to the right. */
export const LivePulse = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
  gap: 10,
  marginLeft: "auto",
});

/** The refresh button on the strip's tile colours; its arrow turns while a fetch is out. */
export const RefreshButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "spinning",
})<{ spinning: boolean }>(({ theme, spinning }) => ({
  color: MASCOT_PALETTE.deepInk,
  backgroundColor: alpha(colors.prime, 0.12),
  boxShadow: `inset 0 0 0 1px ${alpha(colors.prime, 0.28)}`,
  "@media (hover: hover)": { "&:hover": { backgroundColor: alpha(colors.prime, 0.2) } },
  // A thumb-sized target around the small round button.
  "@media (pointer: coarse)": {
    "&::after": { content: '""', position: "absolute", inset: -6 },
  },
  "& svg": { animation: spinning ? "inplay-spin 0.9s linear infinite" : "none" },
  "@keyframes inplay-spin": { to: { transform: "rotate(360deg)" } },
  ...theme.applyStyles("dark", {
    color: MASCOT_PALETTE.sky,
    backgroundColor: alpha(MASCOT_PALETTE.sky, 0.12),
    boxShadow: `inset 0 0 0 1px ${alpha(MASCOT_PALETTE.sky, 0.32)}`,
    "@media (hover: hover)": { "&:hover": { backgroundColor: alpha(MASCOT_PALETTE.sky, 0.2) } },
  }),
  "@media (prefers-reduced-motion: reduce)": { "& svg": { animation: "none" } },
}));
